import React, { useState, useEffect } from 'react';
import './shared/AccountChoice.css';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthPageShell from './AuthPageShell';
import GoogleSignInButton from './shared/GoogleSignInButton';
import {
  getPublicAuthOrigin,
  isDistrictSubdomainHost,
  isOAuthAuthHost,
  isProductionMultiHostAuth
} from '../utils/authOrigin';
import './Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  // One Google identity resolving to more than one account: the person picks a capacity before
  // any session exists.
  const [accountChoice, setAccountChoice] = useState(null);
  const [pendingCredential, setPendingCredential] = useState(null);
  const { login, loginWithGoogle, navigateAfterGoogleLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isProductionMultiHostAuth()) return;
    if (!isOAuthAuthHost() && isDistrictSubdomainHost() && window.location.pathname === '/login') {
      sessionStorage.setItem('auth_return_to', window.location.href);
      window.location.replace(`${getPublicAuthOrigin()}/login`);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const result = await login(username, password);
      if (!result || typeof result !== 'object') {
        setError('Login failed');
        return;
      }

      const user = result.user;
      const userRole = user && typeof user === 'object' ? user.role : undefined;

      if (result.success && userRole) {
        await navigateAfterGoogleLogin(user, navigate);
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err?.message || 'Login failed');
    }
  };

  const handleGoogleCredential = async (credential, accountId) => {
    setError('');
    const result = await loginWithGoogle(credential, 'login', undefined, accountId);

    if (result.chooseAccount) {
      // Hold the Google credential so the second call can name the chosen account without
      // sending the person back through the Google button.
      setPendingCredential(credential);
      setAccountChoice(result.accounts);
      return;
    }

    if (result.success) {
      setAccountChoice(null);
      setPendingCredential(null);
      await navigateAfterGoogleLogin(result.user, navigate);
    } else {
      setError(result.error);
    }
  };

  const ROLE_LABEL = {
    parent: 'Parent account',
    district_admin: 'District administrator',
    district_editor: 'District editor',
    district_user: 'District user',
    superadmin: 'Platform administrator'
  };

  if (accountChoice) {
    return (
      <AuthPageShell>
        <div className="login-box">
          <h1>Back to School Forms</h1>
          <h2>Which account?</h2>
          <p className="login-help">
            This Google account is used for more than one account. Choose the one you want to
            open &mdash; you can switch later without signing in again.
          </p>
          <div className="account-choice-list">
            {accountChoice.map((a) => (
              <button
                key={a.id}
                type="button"
                className="account-choice"
                onClick={() => handleGoogleCredential(pendingCredential, a.id)}
              >
                <span className="account-choice-role">{ROLE_LABEL[a.role] || a.role}</span>
                {a.district_name && <span className="account-choice-sub">{a.district_name}</span>}
              </button>
            ))}
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <div className="login-box">
        <h1>Back to School Forms</h1>
        <h2>Login</h2>
        {error && <div className="error-message">{error}</div>}
        <GoogleSignInButton onCredential={handleGoogleCredential} />
        <p
          style={{
            textAlign: 'center',
            color: '#666',
            fontSize: '0.85rem',
            margin: '0.75rem 0 1rem'
          }}
        >
          or sign in with your username and password
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="login-username">Username</label>
            <input
              id="login-username"
              name="username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn-primary">Login</button>
        </form>
        <div style={{ marginTop: '1rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Link to="/forgot-username" style={{ color: '#667eea', textDecoration: 'none', fontSize: '0.9rem' }}>
            Forgot your username?
          </Link>
          <Link to="/forgot-password" style={{ color: '#667eea', textDecoration: 'none', fontSize: '0.9rem' }}>
            Forgot your password?
          </Link>
        </div>
      </div>
    </AuthPageShell>
  );
};

export default Login;
